import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { useTheme } from '../context/ThemeContext';
import { MapPin, Loader2, Check, X, Edit3 } from 'lucide-react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Service-area bbox covering NJ + DC + MD + VA (lng_min, lat_min, lng_max, lat_max).
// Mirrors backend SERVICE_BBOX in field_quality.py. Suggestions outside this
// envelope are biased OUT of the result list — customers don't see them.
const SERVICE_BBOX = '-79.5,36.5,-73.8,41.4';

// Parse a Mapbox `feature` object into the structured address shape we store on
// the user record. The context array is a stable Mapbox API: each item has an
// `id` like "postcode.123" / "place.456" / "region.789" / "country.012".
function parseFeature(feature) {
  if (!feature) return null;
  const ctx = feature.context || [];
  const find = (prefix) => {
    const hit = ctx.find((c) => typeof c.id === 'string' && c.id.startsWith(prefix));
    return hit || null;
  };
  const place = find('place.');
  const region = find('region.');
  const postcode = find('postcode.');
  const country = find('country.');

  // feature.address is the house number; feature.text is the street name.
  const houseNumber = feature.address || '';
  const street = feature.text || '';
  const line1 = [houseNumber, street].filter(Boolean).join(' ').trim();

  const [lng, lat] = feature.center || [null, null];

  return {
    line1,
    city: (place && place.text) || '',
    state: (region && (region.short_code || '').replace(/^US-/, '')) || (region && region.text) || '',
    postcode: (postcode && postcode.text) || '',
    country: (country && (country.short_code || country.text)) || 'us',
    lat: typeof lat === 'number' ? lat : null,
    lng: typeof lng === 'number' ? lng : null,
    formatted: feature.place_name || line1,
  };
}

function isStructured(v) {
  return v && typeof v === 'object' && !Array.isArray(v) && (v.line1 || v.formatted);
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Start typing your delivery address",
  className = "",
  required = false,
  ...props
}) {
  const { isModern } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [confirmed, setConfirmed] = useState(null); // structured address obj once picked
  const [aptValue, setAptValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync from external value. Accepts either a structured object (new) or a
  // flat string (legacy users editing their profile — gracefully degrade).
  useEffect(() => {
    if (isStructured(value)) {
      setConfirmed(value);
      setAptValue(value.apt || '');
      setInputValue('');
    } else if (typeof value === 'string' && value) {
      // Legacy flat string — show as the input prefill so customer can re-pick.
      setInputValue(value);
      setConfirmed(null);
      setAptValue('');
    } else {
      setInputValue('');
      setConfirmed(null);
      setAptValue('');
    }
  }, [value]);

  // Close suggestions when tapping outside
  useEffect(() => {
    function handleOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (!MAPBOX_TOKEN) {
      console.error('Mapbox token not found');
      return;
    }
    setIsLoading(true);
    try {
      const encodedQuery = encodeURIComponent(query);
      // Bbox biases (does not strictly limit) Mapbox to NJ + DMV. Combined with
      // `types=address` (no POIs / places) the customer rarely sees an Ohio
      // result for "123 Main St" — but we still verify the parsed state code
      // server-side via the field-quality validator.
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?` +
        `access_token=${MAPBOX_TOKEN}&` +
        `autocomplete=true&` +
        `types=address&` +
        `country=us&` +
        `bbox=${SERVICE_BBOX}&` +
        `limit=5`
      );
      if (response.ok) {
        const data = await response.json();
        const features = data.features || [];
        setSuggestions(features);
        setShowSuggestions(features.length > 0);
        setSelectedIndex(-1);
      } else {
        console.error('Geocoding response error:', response.status);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchSuggestions(newValue); }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    const parsed = parseFeature(suggestion);
    if (!parsed) return;
    parsed.apt = aptValue || '';
    setConfirmed(parsed);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(parsed);
  };

  const handleEdit = () => {
    // Customer wants to change the picked address. Surface the input again
    // (pre-filled with whatever they had so they can re-pick).
    const prefill = confirmed ? `${confirmed.line1 || ''}` : '';
    setInputValue(prefill);
    setConfirmed(null);
    onChange(null);
  };

  const handleAptChange = (e) => {
    const v = e.target.value;
    setAptValue(v);
    if (confirmed) {
      const next = { ...confirmed, apt: v };
      setConfirmed(next);
      onChange(next);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // ---- Confirmation card view (after a suggestion is picked) ----
  if (confirmed) {
    return (
      <div className="am-addr space-y-2" data-testid="address-confirmed">
        <div
          className="border border-[#39FF14] bg-[#050D08] p-3 flex items-start gap-3"
          data-testid="address-confirmation-card"
        >
          <Check className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0 font-mono text-sm">
            <div className="text-[#6B8E73] text-[10px] uppercase tracking-widest mb-1">
              Delivering to
            </div>
            <div className="text-[#39FF14]">{confirmed.line1}</div>
            <div className="text-[#A3C7A8] text-xs mt-0.5">
              {[confirmed.city, confirmed.state, confirmed.postcode].filter(Boolean).join(', ')}
            </div>
          </div>
          <button
            type="button"
            onClick={handleEdit}
            className="text-[#A3C7A8] hover:text-[#39FF14] text-xs uppercase tracking-wider font-mono flex items-center gap-1"
            data-testid="address-edit-button"
            title="Use a different address"
          >
            <Edit3 className="w-3 h-3" /> Change
          </button>
        </div>
        <div>
          <Input
            type="text"
            value={aptValue}
            onChange={handleAptChange}
            placeholder="Apt / Unit / Floor (optional)"
            className="am-addr-input bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
            autoComplete="off"
            data-testid="address-apt-input"
          />
        </div>
      </div>
    );
  }

  // ---- Searching view (no selection yet) ----
  return (
    <div className="am-addr space-y-2">
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B6B52]" />
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
            className={`am-addr-input pl-10 pr-10 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none ${className}`}
            autoComplete="off"
            data-testid="address-search-input"
            required={required}
            {...props}
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14] animate-spin" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-[#0C0612] border border-[#39FF14] max-h-60 overflow-auto" data-testid="address-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                onPointerDown={(e) => { e.preventDefault(); handleSelectSuggestion(suggestion); }}
                onClick={(e) => e.preventDefault()}
                className={`w-full px-4 py-4 text-left font-mono text-sm flex items-start gap-3 transition-colors min-h-[56px] active:bg-[#2A0F3D] ${
                  index === selectedIndex ? 'bg-[#2A0F3D] text-[#39FF14]' : 'text-[#A3C7A8] hover:bg-[#111A14]'
                }`}
                data-testid={`address-suggestion-${index}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#39FF14]" />
                <div>
                  <p className="text-[#39FF14]">{suggestion.text}{suggestion.address ? ` · ${suggestion.address}` : ''}</p>
                  <p className="text-[#4B6B52] text-xs mt-0.5">
                    {suggestion.place_name.replace(suggestion.text + ', ', '')}
                  </p>
                </div>
              </button>
            ))}
            <div className="px-4 py-2 border-t border-[#2A0F3D]">
              <p className="text-[#4B6B52] font-mono text-xs">Powered by Mapbox · NJ · DC · MD · VA</p>
            </div>
          </div>
        )}

        {showSuggestions && inputValue.length >= 3 && suggestions.length === 0 && !isLoading && (
          <div className="absolute z-50 w-full mt-1 bg-[#0C0612] border border-[#2A0F3D] p-4">
            <p className="text-[#4B6B52] font-mono text-sm text-center">
              No addresses found
            </p>
          </div>
        )}
      </div>
      <p className="text-[#4B6B52] font-mono text-[10px] uppercase tracking-wider" data-testid="address-hint">
        {isModern ? 'Pick from the dropdown so we deliver to the right spot.' : '// Pick from the dropdown so we deliver to the right spot'}
      </p>
    </div>
  );
}
